// Temporary stub for reward service during Supabase migration
// TODO: Implement with Supabase

import type { Reward } from '@/models/Reward'
import type { EarnedReward } from '@/models/EarnedReward'
import { supabaseRewardService } from './supabase/reward.service'
import { useAnalytics, type ActivationContext } from '@/composables/useAnalytics'

const analytics = useAnalytics()

/**
 * Troisième jalon du parcours d'activation. Les deux portes de création d'une
 * récompense (`addMilestoneReward` côté formulaire, `createReward` côté store)
 * aboutissent au même insert : l'événement est posé sur chacune d'elles, qui ne
 * s'appellent jamais l'une l'autre — aucun double comptage possible.
 */
function trackRewardCreated(reward: any, ctx?: ActivationContext) {
  if (!reward?.id) return
  analytics.logRewardCreated(
    reward.id,
    reward.is_recurring ? 'recurring' : 'unique',
    reward.required_points || 0,
    reward.category,
    { ...ctx, templateId: reward.template_id ?? null }
  )
}

// Define CollectedReward type for consistency with ChildDetailPage.vue
type CollectedReward = EarnedReward & {
  collected_at: string;
  isRecurring?: boolean;
  child_points_at_collection?: number; // Points that the child had when they collected this reward
  status?: string; // Status of the collected reward (e.g., 'active', 'void')
}

const REWARDS_CACHE_TTL_MS = 8000;
const REWARDS_CACHE_MAX_ENTRIES = 40;

function rewardsCacheKey(kind: 'child' | 'collected' | 'household', id: string): string {
  return `${kind}:${id}`;
}

export const rewardService = {
  _rewardsCache: null as Map<string, { data: unknown; ts: number }> | null,

  _getCached<T>(key: string): T | null {
    if (!this._rewardsCache) return null;
    const entry = this._rewardsCache.get(key);
    if (!entry || Date.now() - entry.ts > REWARDS_CACHE_TTL_MS) return null;
    return entry.data as T;
  },

  _setCache(key: string, data: unknown): void {
    if (!this._rewardsCache) this._rewardsCache = new Map();
    if (this._rewardsCache.size >= REWARDS_CACHE_MAX_ENTRIES) {
      const firstKey = this._rewardsCache.keys().next().value;
      if (firstKey !== undefined) this._rewardsCache.delete(firstKey);
    }
    this._rewardsCache.set(key, { data, ts: Date.now() });
  },

  _rewardsInFlight: null as Map<string, Promise<unknown>> | null,

  /**
   * Cache de lecture, avec UN SEUL vol par clé.
   *
   * Le cache n'est écrit qu'APRÈS la réponse : sans ce garde, N appels
   * concurrents sur la même clé partaient tous en requête, puis écrivaient le
   * même résultat à la suite. Le cas arrive dès qu'une page monte plusieurs
   * vues d'un même enfant en même temps.
   */
  async _cachedFetch<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const cached = this._getCached(key) as T | null;
    if (cached !== null) return cached;

    if (!this._rewardsInFlight) this._rewardsInFlight = new Map();
    const pending = this._rewardsInFlight.get(key) as Promise<T> | undefined;
    if (pending) return pending;

    const run = fetcher().then((data) => {
      this._setCache(key, data);
      return data;
    });
    // On ne retire que SON propre vol : une invalidation suivie d'un nouvel
    // appel ne doit pas être effacée par la fin du vol précédent.
    const tracked: Promise<T> = run.finally(() => {
      if (this._rewardsInFlight?.get(key) === tracked) this._rewardsInFlight.delete(key);
    });
    this._rewardsInFlight.set(key, tracked);
    return tracked;
  },

  /** Invalidate all rewards caches. Call when a child obtains a reward or when reward settings change (global or per-reward). */
  invalidateRewardsCache(): void {
    this._rewardsCache?.clear();
    // Un vol en cours porte les données d'avant l'invalidation : le laisser
    // partageable rendrait périmé l'appel qui suit une collecte de récompense.
    this._rewardsInFlight?.clear();
  },

  /** Invalidate cache for a specific child (obtained reward, claim/unclaim). */
  invalidateRewardsCacheForChild(childId: string): void {
    if (!this._rewardsCache) return;
    this._rewardsCache.delete(rewardsCacheKey('child', childId));
    this._rewardsCache.delete(rewardsCacheKey('collected', childId));
    this._rewardsInFlight?.delete(rewardsCacheKey('child', childId));
    this._rewardsInFlight?.delete(rewardsCacheKey('collected', childId));
  },

  /**
   * True when BOTH this child's reward list and collected rewards are cached and
   * fresh — so a consumer can render immediately (and refresh in the background)
   * instead of blocking on a full-page spinner. Used by the per-kid rewards page.
   */
  hasChildRewardsCached(childId: string): boolean {
    return (
      this._getCached(rewardsCacheKey('child', childId)) !== null &&
      this._getCached(rewardsCacheKey('collected', childId)) !== null
    );
  },

  /** Invalidate cache for a household (reward settings, assignments). */
  invalidateRewardsCacheForHousehold(householdId: string): void {
    if (!this._rewardsCache) return;
    this._rewardsCache.delete(rewardsCacheKey('household', householdId));
  },

  // Récupérer les récompenses d'un utilisateur
  async getRewardsByUserId(userId: string): Promise<Reward[]> {
    return supabaseRewardService.getRewardsByUserId(userId)
  },

  // Récupérer les récompenses d'un foyer
  async getHouseholdRewards(householdId: string): Promise<Reward[]> {
    return this._cachedFetch(
      rewardsCacheKey('household', householdId),
      () => supabaseRewardService.getHouseholdRewards(householdId),
    );
  },

  // Récupérer les récompenses pour un enfant spécifique (filtrées par assignation)
  async getRewardsForChild(childId: string): Promise<Reward[]> {
    return this._cachedFetch(
      rewardsCacheKey('child', childId),
      () => supabaseRewardService.getRewardsForChild(childId),
    );
  },

  // Récupérer les récompenses jalons d'un foyer
  async getHouseholdMilestoneRewards(householdId: string): Promise<any[]> {
    return this.getHouseholdRewards(householdId);
  },

  // Récupérer les récompenses disponibles pour un enfant
  // TODO: Implement available rewards for child logic
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getAvailableRewardsForChild(childId: string, userId: string): Promise<Reward[]> {
    return []
  },

  // Récupérer les récompenses gagnées par un enfant
  // TODO: Implement earned rewards logic
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getEarnedRewardsByChild(childId: string): Promise<EarnedReward[]> {
    return []
  },

  // Récupérer les récompenses collectées
  async getCollectedRewards(childId: string): Promise<CollectedReward[]> {
    return this._cachedFetch(
      rewardsCacheKey('collected', childId),
      async () => (await supabaseRewardService.getCollectedRewards(childId)) as unknown as CollectedReward[],
    );
  },

  // Vérifier les récompenses gagnées
  // TODO: Implement check for earned rewards logic
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async checkForEarnedRewards(childId: string, userId: string): Promise<EarnedReward[]> {
    return []
  },

  // Réclamer une récompense collectée
  async claimCollectedReward(childId: string, earnedRewardId: string): Promise<void> {
    await supabaseRewardService.claimCollectedReward(earnedRewardId);
    this.invalidateRewardsCacheForChild(childId);
  },

  // Réclamer une récompense (alias pour claimCollectedReward)
  async claimReward(rewardId: string): Promise<void> {
    await supabaseRewardService.claimCollectedReward(rewardId);
    this.invalidateRewardsCache();
  },

  // Annuler la réclamation d'une récompense
  async unclaimCollectedReward(childId: string, earnedRewardId: string): Promise<boolean> {
    const ok = await supabaseRewardService.unclaimCollectedReward(earnedRewardId);
    if (ok) this.invalidateRewardsCacheForChild(childId);
    return ok;
  },

  // Alias pour unclaimCollectedReward
  async unclaimReward(rewardId: string): Promise<boolean> {
    const ok = await supabaseRewardService.unclaimCollectedReward(rewardId);
    if (ok) this.invalidateRewardsCache();
    return ok;
  },

  // Delete a collected reward instance
  async deleteCollectedReward(collectedRewardId: string): Promise<boolean> {
    const ok = await supabaseRewardService.deleteCollectedReward(collectedRewardId);
    if (ok) this.invalidateRewardsCache();
    return ok;
  },

  // Ajouter une récompense jalon
  async addMilestoneReward(householdId: string, data: any, ctx?: ActivationContext): Promise<any> {
    const reward = await supabaseRewardService.createReward(data)
    trackRewardCreated(reward, ctx)
    return reward
  },

  // Supprimer une récompense jalon
  // TODO: Implement delete milestone reward if needed
  async deleteMilestoneReward(householdId: string, rewardId: string): Promise<void> {
    await supabaseRewardService.deleteReward(rewardId)
    // Porte d'entrée réelle des écrans de récompenses : sans elle, la
    // suppression resterait invisible côté mesure.
    analytics.logRewardDeleted(rewardId)
  },

  // Récupérer les récompenses d'un enfant
  // TODO: Implement get child rewards logic
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getChildRewards(childId: string): Promise<Reward[]> {
    return []
  },

  // Créer une nouvelle récompense
  async createReward(data: any, ctx?: ActivationContext): Promise<Reward> {
    const reward = await supabaseRewardService.createReward(data);
    if (data.household_id) this.invalidateRewardsCacheForHousehold(data.household_id);
    this.invalidateRewardsCache();
    trackRewardCreated(reward, ctx);
    return reward;
  },

  // Mettre à jour une récompense
  async updateReward(rewardId: string, data: any): Promise<any> {
    const reward = await supabaseRewardService.updateReward(rewardId, data);
    if (data.household_id) this.invalidateRewardsCacheForHousehold(data.household_id);
    this.invalidateRewardsCache();
    return reward;
  },

  // Supprimer une récompense
  async deleteReward(rewardId: string): Promise<void> {
    await supabaseRewardService.deleteReward(rewardId);
    this.invalidateRewardsCache();
    analytics.logRewardDeleted(rewardId);
  },

  // Assign milestone rewards when a child reaches required points (enfant a obtenu une récompense)
  async assignMilestoneRewards(childId: string, householdId: string, childPoints: number): Promise<{ unlockedRewards: string[] } | void> {
    const result = await supabaseRewardService.assignMilestoneRewards(childId, householdId, childPoints);
    this.invalidateRewardsCacheForChild(childId);
    if (householdId) this.invalidateRewardsCacheForHousehold(householdId);
    return result;
  },

  // Collect the next instance of a recurring reward (for "Récupérer cette instance" button)
  async collectNextRewardInstance(childId: string, rewardId: string, childPoints: number): Promise<{ id: string | null; error?: string }> {
    const result = await supabaseRewardService.collectNextRewardInstance(childId, rewardId, childPoints);
    this.invalidateRewardsCacheForChild(childId);
    return result;
  },

  async setRewardAssignments(rewardId: string, childIds: string[]): Promise<void> {
    await supabaseRewardService.setRewardAssignments(rewardId, childIds);
    this.invalidateRewardsCache();
  },

  // Get available collective rewards
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async getAvailableCollectiveRewards(householdId: string): Promise<any[]> {
    console.warn('Reward service method not yet implemented');
    return [];
  },

  // Check for collective rewards
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async checkForCollectiveRewards(householdId: string): Promise<any[]> {
    console.warn('Reward service method not yet implemented');
    return [];
  }
};
